import React, { useRef } from "react";
import ScrollModal from "../ui/ScrollModal.jsx";
import { Calculator, ArrowUpRight } from "lucide-react";
import CTALarge from "../CTA/CTALarge.jsx";
import BannerCTA from "../CTA/CTABanner.jsx";
import { ImageInline } from "../GetStarted/GetStartedHelpers.jsx";
import Seo from "../Seo.jsx";
import guidesConfig from "../../config/guidesConfig";
import Tooltip from "./Tooltip";
import "./guides.css";
import fractional from "./images/fractional-odds.png";
import decimal from "./images/decimal-odds.png";


const HowToReadOddsGuide = () => {
  const scrollRef = useRef(null);
  const meta = guidesConfig.howToReadOddsGuide?.seo || {};

  return (
    <div className="guides-content">
            <Seo title={meta.title} description={meta.description} url={guidesConfig.howToReadOddsGuide?.route || "/guides"}/>

      <p>
        Understanding how to read betting odds is essential for matched betting. In this guide, we’ll walk you through what odds mean, how to read different odds formats, and how to switch between them quickly.
      </p>

      <h2>What Are Betting Odds?</h2>
      <h6 className="info">
        Betting odds represent the chance of an outcome happening, and how much you’ll win if your bet succeeds.
      </h6>
      <p>
        Bookmakers set odds based on how likely something is to happen, but they also include a built-in margin to stay profitable.
      </p>
      <h6 className="warn">Bookmaker odds rarely reflect true probability</h6>
      <p>
        In matched betting, odds are key for finding profitable offers, locking in risk-free returns, and calculating the right stakes.
      </p>

      <h2>What Types of Odds Formats Are There?</h2>
      <p>The three most common formats are:</p>
      <ul>
        <li><span className="key">Fractional odds</span> (e.g. 5/1)</li>
        <li><span className="key">Decimal odds</span> (e.g. 6.00)</li>
        <li><span className="key">American odds</span> (e.g. +500 or -200)</li>
      </ul>
      <p>Let’s break each one down.</p>

      <h2>How to Read Fractional Odds</h2>
      <p>
        <strong>Fractional odds</strong> are traditional in the UK. They show how much you’ll win relative to your <Tooltip content="Your initial bet.">stake</Tooltip>.
      </p>
      <p>The number on the right is how much you <em>put in</em> (your <Tooltip content="Your initial bet.">stake</Tooltip>).</p>
      <p>The number on the left is how much you <em>get out</em>.</p>

      <h6 className="info">Odds of <strong>5/1</strong> mean you’ll win £5 for every £1 you bet.</h6>
      <ImageInline
          imageSrc={fractional}
          alt="fractional odds Betfair"
          annotations={
            <div>
                <p><strong>If you bet on...</strong></p>
                <ul>
                    <li>Brentford → Win £15 for every £8 staked</li>
                    <li>Draw → Win £2 for every £1 staked</li>
                    <li>Chelsea → Win £11 for every £10 staked</li>
                </ul>
            </div>
          }
        />      
      <h6 className="info">
        You get your <Tooltip content="Your initial bet.">stake</Tooltip> back, too. If you bet £1 on a draw at 2/1, your <Tooltip content="Your winnings plus your returned stake.">total return</Tooltip> would be £2 (winnings) + £1 (stake) = £3.
      </h6>

      <h2>How to Read Decimal Odds</h2>
    <p>
    <strong>Decimal odds</strong> are the standard format on exchanges like Betfair. They’re easier to calculate with than fractional odds.
    </p>
    <p>
    The number itself tells you your <em>total return</em> — <Tooltip content="Your winnings plus your returned stake.">winnings + stake</Tooltip>.
    </p>
    <p>
    So if the decimal is <strong>2.00</strong>, you're getting back exactly double your <Tooltip content="Your initial bet.">stake</Tooltip>. If it's <strong>6.00</strong>, you get back 6x your stake.
    </p>

    <h6 className="info">
    Odds of <strong>6.00</strong> mean you’ll get £6 back for every £1 bet. That includes £5 profit + £1 stake.
    </h6>

    <ImageInline
    imageSrc={decimal}
    alt="decimal odds example on Betfair"
    annotations={
        <div>
        <p><strong>If you bet on...</strong></p>
        <ul>
            <li>Brentford → £10 returns £28.80 including your <Tooltip content="Your initial bet.">stake</Tooltip></li>
            <li>Draw → £10 returns £30.00 including your <Tooltip content="Your initial bet.">stake</Tooltip></li>
            <li>Chelsea → £10 returns £21.00 including your <Tooltip content="Your initial bet.">stake</Tooltip></li>
        </ul>
        </div>
    }
    />

    <h6 className="info">
    Decimal odds <strong>always include</strong> your <Tooltip content="Your initial bet.">stake</Tooltip>. If you bet £1 at 4.00, your <Tooltip content="Your winnings plus your returned stake.">total return</Tooltip> is £4 — that's £3 profit + £1 stake.
    </h6>

    <h6 className="info">
    Matched bettors prefer decimals because they’re easier to compare, especially on exchanges and in <Tooltip content="Tools that help calculate your stakes and profit/loss when matched betting.">calculators</Tooltip>.
    </h6>

    <ul>
    <li>Simpler maths</li>
    <li>Better for comparing prices between bookmakers and exchanges</li>
    <li>Used in all matched betting calculators</li>
    </ul>
    <h6 className="warn">Fractional odds at bookmakers are not always accurate. They often approximate to a simpler fraction. This is true for our Brentford example where the fractional odds suggest 2.75 odds in decimal, but the decimal odds, which take precedence, are 2.88!</h6>
    <h6 className="info">
    Most bookies use fractional odds by default. You can usually switch to decimal in the settings.
    </h6>
    <ScrollModal scrollContainerRef={scrollRef} props={{ctaKey: "om2"}} />


      <h2>What are American Odds (Moneyline)?</h2>
      <p>
        You’ll rarely need <strong>American odds</strong> unless you're on a US-facing site, but it's good to recognise them.
      </p>
      <ul>
        <li><strong>+200</strong> → Win £200 from a £100 stake</li>
        <li><strong>-200</strong> → Bet £200 to win £100</li>
      </ul>
      <p>
        If you need to convert them, our odds converter tool makes it easy.
      </p>

      <CTALarge 
        to="/calculators/odds-converter" 
        text="Go to the Odds Converter" 
        icon={Calculator} 
      />

      <h2>What Is Implied Probability?</h2>
      <h6 className="info">
        Implied probability gives you a rough idea of the chance of an outcome, based on the odds.
      </h6>
      <p>Remember, bookies build in a profit margin, so the actual probability will be a little less than the odds suggest.</p>
      <h3>For Decimal Odds:</h3>
      <p><strong>Divide 1 by the decimal odds, then multiply by 100</strong></p>
      <p>Example: 4.00 → (1 ÷ 4) × 100 = <strong>25%</strong></p>

      <h3>For Fractional Odds:</h3>
      <p><strong>Divide the number on the right by the sum of both numbers, then multiply by 100</strong></p>
      <p>Example: 5/1 → 1 ÷ (5+1) = 0.166 → <strong>16.6%</strong></p>

      <h2>Final Thoughts</h2>
      <p>
        Understanding odds is the foundation of matched betting. Whether it’s placing a{" "}
        <Tooltip content="The first bet you place to qualify for an offer.">qualifying bet</Tooltip>, catching a{" "}
        <Tooltip content="An enhanced price from a bookmaker, usually for a limited time.">price boost</Tooltip>, or calculating a{" "}
        <Tooltip content="The amount you need to stake on the exchange to cover your back bet.">lay stake</Tooltip>, odds are always involved.
      </p>
      <p>
        Keep our odds converter handy — it’s the quickest way to compare formats, see value, and make smart betting decisions.
      </p>

      <BannerCTA 
        to="/calculators/odds-converter" 
        text="Try the Odds Converter" 
        icon={ArrowUpRight} 
      />
    </div>
  );
};

export default HowToReadOddsGuide;
